import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { motion } from "framer-motion";
import { CheckCircle2, AlertCircle, Loader2, Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Card, CardContent } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { ensureTurnstileScript } from "@/lib/turnstile";
import {
  useSubmitContact,
  type SubmitContactMutationError,
} from "@workspace/api-client-react";

// `window.turnstile` / `window.onTurnstileSuccess` are typed in
// src/types/turnstile.d.ts (shared with FounderEmailReveal).

const contactSchema = z.object({
  name: z.string().min(1, "Name is required").max(200),
  email: z.string().email("Please enter a valid email address").min(3).max(320),
  company: z.string().max(200).optional(),
  message: z.string().min(1, "Message is required").max(5000),
});

type ContactFormValues = z.infer<typeof contactSchema>;

export function Contact() {
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const [isSuccess, setIsSuccess] = useState(false);
  const submitContact = useSubmitContact();
  const siteKey = import.meta.env.VITE_TURNSTILE_SITE_KEY;

  const form = useForm<ContactFormValues>({
    resolver: zodResolver(contactSchema),
    defaultValues: {
      name: "",
      email: "",
      company: "",
      message: "",
    },
  });

  useEffect(() => {
    // Inject the Turnstile script once (shared with FounderEmailReveal).
    if (siteKey) ensureTurnstileScript();

    // Set up global callback for Turnstile
    window.onTurnstileSuccess = (token: string) => {
      setTurnstileToken(token);
    };

    return () => {
      delete window.onTurnstileSuccess;
    };
  }, [siteKey]);

  const onSubmit = (data: ContactFormValues) => {
    if (!turnstileToken) {
      form.setError("root", {
        type: "manual",
        message: "Please complete the security check.",
      });
      return;
    }

    submitContact.mutate(
      {
        data: {
          name: data.name,
          email: data.email,
          company: data.company || null,
          message: data.message,
          turnstileToken,
        },
      },
      {
        onSuccess: () => {
          setIsSuccess(true);
        },
        onError: (err: SubmitContactMutationError) => {
          const message =
            (err as { data?: { error?: string } } | undefined)?.data?.error ??
            "An unexpected error occurred. Please try again.";
          form.setError("root", {
            type: "server",
            message,
          });
          // Token is single-use; clear and reset the widget so the user
          // can retry without a stale token.
          setTurnstileToken(null);
          window.turnstile?.reset();
        },
      }
    );
  };

  return (
    <section id="contact" className="py-24 md:py-32 relative bg-secondary/20">
      <div className="container mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-24">
          <div>
            <h2 className="text-primary font-mono text-sm tracking-widest uppercase mb-3">Initiate Contact</h2>
            <h3 className="text-3xl md:text-5xl font-display font-bold text-foreground mb-6">
              Ready to chart your course?
            </h3>
            <p className="text-lg text-muted-foreground mb-8 leading-relaxed">
              Whether you need to automate a specific bottleneck or entirely re-architect your operations, we're ready to build. Tell us about your business and where you're feeling the most friction.
            </p>
            
            <div className="space-y-6">
              <div className="flex items-center gap-4 text-muted-foreground">
                <div className="w-10 h-10 rounded-full bg-card flex items-center justify-center border border-border">
                  <span className="text-primary text-xl">📍</span>
                </div>
                <span>Houston, Texas</span>
              </div>
            </div>
          </div>

          <div>
            <Card className="bg-card border-border/50 shadow-xl shadow-black/20">
              <CardContent className="p-8">
                {isSuccess ? (
                  <motion.div 
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="flex flex-col items-center justify-center text-center py-12"
                  >
                    <div className="w-16 h-16 bg-primary/20 rounded-full flex items-center justify-center mb-6">
                      <CheckCircle2 className="w-8 h-8 text-primary" />
                    </div>
                    <h4 className="text-2xl font-display font-bold text-foreground mb-3">Transmission Received</h4>
                    <p className="text-muted-foreground">
                      Thank you for reaching out. A member of our team will review your message and contact you shortly.
                    </p>
                    <Button 
                      className="mt-8" 
                      variant="outline"
                      onClick={() => {
                        form.reset();
                        setIsSuccess(false);
                        setTurnstileToken(null);
                        // Reset turnstile widget if it exists
                        window.turnstile?.reset();
                      }}
                    >
                      Send Another Message
                    </Button>
                  </motion.div>
                ) : (
                  <Form {...form}>
                    <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-5">
                      {form.formState.errors.root && (
                        <Alert variant="destructive" className="mb-6 bg-destructive/10 border-destructive/20 text-destructive-foreground">
                          <AlertCircle className="h-4 w-4" />
                          <AlertDescription>
                            {form.formState.errors.root.message}
                          </AlertDescription>
                        </Alert>
                      )}

                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                        <FormField
                          control={form.control}
                          name="name"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel className="text-foreground">Full Name</FormLabel>
                              <FormControl>
                                <Input placeholder="Jane Doe" className="bg-background border-border/50 focus:border-primary/50" {...field} />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={form.control}
                          name="email"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel className="text-foreground">Email Address</FormLabel>
                              <FormControl>
                                <Input type="email" placeholder="jane@example.com" className="bg-background border-border/50 focus:border-primary/50" {...field} />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>

                      <FormField
                        control={form.control}
                        name="company"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel className="text-foreground">Company Name <span className="text-muted-foreground font-normal">(Optional)</span></FormLabel>
                            <FormControl>
                              <Input placeholder="Acme Corp" className="bg-background border-border/50 focus:border-primary/50" {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="message"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel className="text-foreground">Project Details</FormLabel>
                            <FormControl>
                              <Textarea 
                                placeholder="Tell us about the problems you're trying to solve..." 
                                className="min-h-[120px] bg-background border-border/50 focus:border-primary/50 resize-y" 
                                {...field} 
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      {siteKey && (
                        <div className="py-2 flex justify-center sm:justify-start">
                          <div 
                            className="cf-turnstile" 
                            data-sitekey={siteKey}
                            data-callback="onTurnstileSuccess"
                          ></div>
                        </div>
                      )}

                      <Button 
                        type="submit" 
                        size="lg" 
                        className="w-full sm:w-auto h-12 px-8"
                        disabled={submitContact.isPending}
                      >
                        {submitContact.isPending ? (
                          <>
                            <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                            Transmitting...
                          </>
                        ) : (
                          <>
                            Send Message
                            <Send className="w-4 h-4 ml-2" />
                          </>
                        )}
                      </Button>
                    </form>
                  </Form>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </section>
  );
}